import { useEffect, useState } from "react";
import { Copy, Quote } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, fmtDate } from "@/components/Bits";
import { FEATURES } from "@/pages/member/Survey";
import { BetaFeedbackAdmin } from "@/components/BetaFeedbackAdmin";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-border bg-card p-6 min-w-0" data-testid={testId}>
    <div className="font-serif text-4xl text-navy">{value ?? "–"}</div>
    <div className="mt-2 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
  </div>
);

const Testimonial = ({ r, k, label, featured, onToggle }) => {
  const text = r.feedback[k];
  const quote = `"${text}" (${r.user_name.split(" ")[0]}, ${r.business_name || "independent restaurant"})`;
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`testimonial-${r.id}-${k}`}>
      <Quote className="h-4 w-4 text-pinkb" />
      <p className="mt-2 text-sm leading-relaxed">{text}</p>
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>{r.user_name} · {r.business_name || "—"} · {label} · {r.ratings[k]}/5{r.photo_path && " · photo added"}</span>
        <div className="flex items-center gap-3">
          <button onClick={onToggle} data-testid={`testimonial-feature-${r.id}-${k}`}
            className={`rounded-full border px-3 py-1 transition-colors ${featured ? "bg-pinkb text-white border-pinkb" : "border-border hover:border-pinkb"}`}>
            {featured ? "On home page" : "Show on home page"}
          </button>
          <button onClick={() => navigator.clipboard.writeText(quote).then(() => toast.success("Testimonial copied"))} className="hover:text-pinkb" data-testid={`testimonial-copy-${r.id}-${k}`}><Copy className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
};

export default function AdminSurveys() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/surveys").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (survey_id, feature) => {
    try {
      const { data } = await api.post("/admin/testimonials/toggle", { survey_id, feature });
      toast.success(data.featured ? "Now showing on your home page" : "Removed from home page");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return null;
  const quotes = d.responses.filter((r) => r.testimonial_ok).flatMap((r) => FEATURES.filter(([k]) => r.feedback[k]?.trim()).map(([k, l]) => ({ r, k, l })));
  return (
    <div>
      <PageHeader eyebrow={`${d.count} responses`} title="Feedback & testimonials." />
      <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4 mb-10">
        {FEATURES.map(([k, l]) => <Stat key={k} label={`${l} avg /5`} value={d.avg_ratings[k]} testId={`survey-avg-${k}`} />)}
        <Stat label="Net promoter score" value={d.nps} testId="survey-nps" />
        <Stat label="Responses" value={d.count} testId="survey-responses" />
      </div>
      <BetaFeedbackAdmin />
      <h2 className="text-2xl text-navy mb-4">Testimonials you can use</h2>
      <div className="grid md:grid-cols-2 gap-4 mb-12">
        {quotes.map(({ r, k, l }) => <Testimonial key={`${r.id}-${k}`} r={r} k={k} label={l} featured={d.featured.includes(`${r.id}:${k}`)} onToggle={() => toggle(r.id, k)} />)}
        {quotes.length === 0 && <p className="text-sm text-muted-foreground">No testimonials with permission yet.</p>}
      </div>
      <h2 className="text-2xl text-navy mb-4">All responses</h2>
      <div className="space-y-4">
        {d.responses.map((r) => (
          <details key={r.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`survey-response-${r.id}`}>
            <summary className="cursor-pointer flex flex-wrap justify-between gap-3 text-sm">
              <span className="font-medium">{r.user_name} · {r.business_name || "—"}</span>
              <span className="font-mono text-xs text-muted-foreground">{r.month} · NPS {r.nps} · {fmtDate(r.created_at)}</span>
            </summary>
            <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
              {FEATURES.map(([k, l]) => <div key={k}><div className="text-xs font-mono uppercase text-muted-foreground">{l} · {r.ratings[k]}/5</div><p className="mt-1">{r.feedback[k] || "—"}</p></div>)}
            </div>
            <div className="mt-4 text-sm space-y-1">
              <p><span className="text-muted-foreground">Most valuable:</span> {r.most_valuable || FEATURES.find(([k]) => k === r.most_useful)?.[1] || "—"}</p>
              <p><span className="text-muted-foreground">Would make it better:</span> {r.missing || "—"}</p>
              <p><span className="text-muted-foreground">Recommend? Why:</span> {r.recommend_why || "—"}</p>
              <p><span className="text-muted-foreground">Comments:</span> {r.comments || "—"}</p>
              <p><span className="text-muted-foreground">Testimonial permission:</span> {r.testimonial_ok ? "Yes" : "No"}</p>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}
